import {
  Badge,
  Banner,
  BlockStack,
  Card,
  InlineStack,
  Page,
  Text,
} from '@shopify/polaris';
import type {PagePropsBase} from '@slj/ui/document';
import {AppFrame} from '@slj/ui/app-frame';

import {AppAccessForm, type AppToggle} from './access-form';
import type {ChromeProps} from './dashboard';
import {identityAccount, identitySections} from './nav';

export interface UserAccessProps extends PagePropsBase {
  chrome: ChromeProps;
  subject: {id: string; name: string; email: string; isAdmin: boolean};
  apps: AppToggle[];
  /** Groups the user belongs to — they also confer app access (managed on the group). */
  groups: {id: string; name: string}[];
  ok?: string;
}

export function UserAccessPage({chrome, subject, apps, groups, ok}: UserAccessProps) {
  const {user} = chrome;

  return (
    <AppFrame
      logoUrl="/assets/logo.svg"
      user={user}
      sections={identitySections(user.isAdmin)}
      currentPath={chrome.path}
      account={identityAccount}
    >
      <Page
        title={subject.name}
        subtitle={subject.email}
        backAction={{content: 'People', url: '/admin'}}
      >
        <BlockStack gap="500">
          {ok ? <Banner tone="success"><p>{ok}</p></Banner> : null}

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Direct app access
              </Text>
              {subject.isAdmin ? (
                <Banner tone="info">
                  <p>
                    This person is an <strong>admin</strong> — they can use every app, and
                    every admin surface, regardless of grants. Make them a user first to
                    scope their access.
                  </p>
                </Banner>
              ) : (
                <>
                  <Text as="p" tone="subdued">
                    Apps granted to this person directly. Access from a group is shown below
                    and managed on the group.
                  </Text>
                  <AppAccessForm action={`/admin/users/${subject.id}/access`} apps={apps} />
                </>
              )}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Groups
              </Text>
              {groups.length === 0 ? (
                <Text as="p" tone="subdued">
                  Not in any group.
                </Text>
              ) : (
                <InlineStack gap="200" wrap>
                  {groups.map((g) => (
                    <a key={g.id} href={`/admin/groups/${g.id}`} style={{textDecoration: 'none'}}>
                      <Badge>{g.name}</Badge>
                    </a>
                  ))}
                </InlineStack>
              )}
            </BlockStack>
          </Card>
        </BlockStack>
      </Page>
    </AppFrame>
  );
}
